/**
 * نظام توزيع العملاء تلقائياً على السيرفرات
 * يختار السيرفر الأنسب بناءً على عدد العملاء النشطين
 */

import { collection, query, where, getDocs, getCountFromServer, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// القيم الافتراضية للسيرفرات (تُستخدم عند عدم وجود بيانات في Firestore)
const DEFAULT_SERVERS = [
  {
    id: 'S1',
    name: 'السيرفر الرئيسي',
    ip: '161.97.177.178',
    port: 3001,
    domain: 'api-daf.dafagents.com',
    maxClients: 10, // حد أقصى للعملاء (لأنه يشغل n8n)
    priority: 1 // أولوية عالية
  },
];

/**
 * جلب قائمة السيرفرات من Firestore
 */
async function getAvailableServers() {
  try {
    const serversDoc = await getDoc(doc(db, 'system_config', 'servers'));
    
    if (serversDoc.exists()) {
      return serversDoc.data().list || DEFAULT_SERVERS;
    } else {
      // إنشاء المستند بالقيم الافتراضية
      await setDoc(doc(db, 'system_config', 'servers'), { list: DEFAULT_SERVERS });
      return DEFAULT_SERVERS;
    }
  } catch (error) {
    console.error('Error fetching servers from Firestore:', error);
    return DEFAULT_SERVERS; // استخدام القيم الافتراضية عند حدوث خطأ
  }
}

// متغير للتخزين المؤقت
let AVAILABLE_SERVERS = null;

/**
 * حساب عدد العملاء النشطين على سيرفر معين
 */
async function getActiveClientsCount(serverId) {
  try {
    const clientsRef = collection(db, 'clients_v2');
    const q = query(
      clientsRef,
      where('server_id', '==', serverId),
      where('isActive', '==', true)
    );
    
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error(`Error counting clients for ${serverId}:`, error);
    return 0;
  }
}

/**
 * الحصول على أفضل سيرفر متاح
 * @returns {Object} معلومات السيرفر المختار
 */
export async function getOptimalServer() {
  try {
    // جلب قائمة السيرفرات
    const servers = await getAvailableServers();
    
    // ترتيب السيرفرات حسب الأولوية
    const sortedServers = [...servers].sort((a, b) => a.priority - b.priority);
    
    // البحث عن أول سيرفر متاح
    for (const server of sortedServers) {
      const activeClients = await getActiveClientsCount(server.id);
      
      console.log(`Server ${server.id}: ${activeClients}/${server.maxClients} clients`);
      
      if (activeClients < server.maxClients) {
        return {
          server_id: server.id,
          server_name: server.name,
          server_url: `http://${server.ip}:${server.port}`,
          whatsapp_api_url: `https://${server.domain}/send`,
          current_load: activeClients,
          max_capacity: server.maxClients,
          available_slots: server.maxClients - activeClients
        };
      }
    }
    
    // لو كل السيرفرات ممتلئة
    throw new Error('جميع السيرفرات ممتلئة! يرجى إضافة سيرفر جديد.');
    
  } catch (error) {
    console.error('Error in getOptimalServer:', error);
    throw error;
  }
}

/**
 * الحصول على إحصائيات جميع السيرفرات
 */
export async function getAllServersStats() {
  try {
    // جلب قائمة السيرفرات
    const servers = await getAvailableServers();
    const stats = [];
    
    for (const server of servers) {
      const activeClients = await getActiveClientsCount(server.id);
      const usagePercent = Math.round((activeClients / server.maxClients) * 100);
      
      stats.push({
        id: server.id,
        name: server.name,
        domain: server.domain,
        activeClients,
        maxClients: server.maxClients,
        availableSlots: server.maxClients - activeClients,
        usagePercent,
        status: usagePercent >= 100 ? 'ممتلئ' : usagePercent >= 80 ? 'شبه ممتلئ' : 'متاح'
      });
    }
    
    return stats;
  } catch (error) {
    console.error('Error in getAllServersStats:', error);
    throw error;
  }
}

/**
 * تحديث قائمة السيرفرات في Firestore
 */
export async function updateServers(serversList) {
  try {
    await setDoc(doc(db, 'system_config', 'servers'), { list: serversList });
    AVAILABLE_SERVERS = null; // مسح الكاش لإعادة التحميل
    console.log('Servers list updated successfully');
  } catch (error) {
    console.error('Error updating servers:', error);
    throw error;
  }
}

export default {
  getOptimalServer,
  getAllServersStats,
  getAvailableServers,
  updateServers
};

